import { type ReactNode, useEffect, useId, useRef } from 'react';
import { Button } from './Button';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    // Focus the first form field (or the dialog itself) so keyboard users land inside.
    const first = contentRef.current?.querySelector<HTMLElement>(
      '.modal-body input:not([type=hidden]):not(:disabled), .modal-body select:not(:disabled), .modal-body textarea:not(:disabled)',
    );
    (first ?? contentRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;

  // Overlay clicks intentionally do not close: a stray click must not discard a
  // half-filled form or signature. Close via ×, Escape, or the footer buttons.
  return (
    <div className="modal-overlay">
      <div
        ref={contentRef}
        tabIndex={-1}
        className={`modal-content modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {title && (
          <div className="modal-header">
            <h3 id={titleId}>{title}</h3>
            <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(44, 44, 44, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-md);
          z-index: 1000;
        }
        .modal-content {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-md);
          max-height: 90vh;
          overflow: auto;
          width: 100%;
          outline: none;
        }
        .modal-sm { max-width: 400px; }
        .modal-md { max-width: 560px; }
        .modal-lg { max-width: 800px; }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-lg);
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h3 { color: var(--color-forest); }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-muted);
          line-height: 1;
          padding: var(--space-xs);
        }
        .modal-body { padding: var(--space-lg); }
        .modal-footer {
          padding: var(--space-md) var(--space-lg);
          border-top: 1px solid var(--color-border);
          display: flex;
          gap: var(--space-sm);
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Go back',
  danger = false,
  loading = false,
  confirmDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** Dismiss button text. Avoid "Cancel" next to actions like "Cancel trip". */
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  /** Disable the confirm button, e.g. while a precondition is still loading. */
  confirmDisabled?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{cancelLabel}</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {typeof message === 'string' ? <p>{message}</p> : message}
    </Modal>
  );
}
